import { InputField, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { LinearLoader, LoadingDots } from '@advanza/ui'
import ServicesSelect from 'components/services/ServicesSelect'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { cleanFilters, clearUrlParameters, updateUrlWithFilters } from './utils'

// Type definitions
export type FilterPrimitive = string | number | null

export type FilterValue = FilterPrimitive | FilterPrimitive[] | undefined

export type FilterRecord = Record<string, FilterValue>

interface RootState {
    services?: {
        isFetching: boolean
        entities?: {
            services: Array<{ id: string; name: string }>
        }
    }
}

export interface FilterField {
    key: string
    type: 'select' | 'date' | 'text' | 'custom' | 'service'
    placeholder?: string
    options?: Array<{ value: string | number; name: string }>
    width?: string
    customComponent?: React.ReactNode
}

interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

interface ReportFilterProps {
    fields: FilterField[]
    initialFilters: FilterRecord
    pagination: Pagination | null
    onPageChange: (page: number) => void
    fetchData: (filters: FilterRecord, page?: number) => void
    isLoading: boolean
    showPagination?: boolean
    onUrlUpdate?: (field: string, value: FilterValue) => void
}

// Extract URL parsing logic into a reusable function
const parseUrlFilters = (
    fields: FilterField[],
    queryParams: FilterRecord,
    initialFilters: FilterRecord,
): FilterRecord => {
    // Helper function to normalize any value to a clean array
    const normalizeToArray = (value: FilterValue): string[] => {
        const values = Array.isArray(value) ? value : [value]
        return values
            .flatMap((v) => String(v).split(','))
            .map((v) => v.trim())
            .filter((v) => v !== '')
    }

    // Helper function to parse a single field value based on its type
    const parseValue = (field: FilterField, urlValue: FilterValue) => {
        if (!urlValue) {
            return initialFilters[field.key] || ''
        }

        switch (field.type) {
            case 'service':
                return normalizeToArray(urlValue)
            case 'select':
                const numValue = Number(urlValue)
                return isNaN(numValue) ? String(urlValue) : numValue
            default:
                return String(urlValue)
        }
    }

    // Build the result using functional approach
    return Object.fromEntries(
        fields.map((field) => [field.key, parseValue(field, queryParams[field.key])]),
    )
}

const ReportFilter = ({
    fields,
    initialFilters,
    pagination,
    onPageChange,
    fetchData,
    isLoading,
    showPagination = true,
    onUrlUpdate,
}: ReportFilterProps) => {
    const location = useLocation()
    const query = queryString.parse(location.search, {
        arrayFormat: 'bracket', // Parse bracket format arrays correctly
        parseBooleans: true,
        parseNumbers: true,
    })

    // Get services loading state
    const servicesLoading = useSelector((state: RootState) => state.services?.isFetching ?? false)
    const servicesData = useSelector((state: RootState) => state.services?.entities?.services ?? [])

    // Initialize filters from URL on component mount
    const [filters, setFilters] = useState(() =>
        parseUrlFilters(fields, query as FilterRecord, initialFilters),
    )

    useEffect(() => {
        // Update filters when URL changes
        const newFilters = parseUrlFilters(fields, query as FilterRecord, initialFilters)
        setFilters(newFilters)

        // Trigger data fetch with the new filters
        const cleanedFilters = cleanFilters(newFilters)

        // Always fetch data - either with filters or without (clean page load)
        fetchData(cleanedFilters)
    }, [location.search])

    const handleFilterChange = (field: string, value: FilterValue) => {
        // Handle array values
        let filterValue = value
        if (Array.isArray(value)) {
            // For service_id, keep as array; for other fields, convert to strings
            if (field === 'service_id') {
                filterValue = value.map((v) => String(v)).filter((v) => v !== '') // Remove empty values
            } else {
                filterValue = value.map((v) => String(v))
            }
        }

        // Special handling for numeric fields to convert NaN to empty string
        // Only apply this to provider_status which should be numeric
        if (field === 'provider_status' && (isNaN(Number(value)) || value === '')) {
            filterValue = ''
        }

        // Handle NaN values for any field
        if (value === 'NaN' || (typeof value === 'number' && isNaN(value))) {
            filterValue = ''
        }

        const newFilters = { ...filters, [field]: filterValue }
        setFilters(newFilters)

        // Update URL - use callback if provided, otherwise update directly
        if (onUrlUpdate) {
            // For service_id field, pass as array to match expected URL format
            const urlValue: FilterValue =
                field === 'service_id' ? ([filterValue] as FilterValue) : filterValue
            onUrlUpdate(field, urlValue)
        } else {
            // Get current query parameters to preserve existing filters
            // Use window.location.search to get the most current URL state
            const currentQuery = queryString.parse(window.location.search)

            // For service_id field, pass as array to match expected URL format
            const urlValue: FilterValue =
                field === 'service_id' ? ([filterValue] as FilterValue) : filterValue

            // Update URL with new filters
            updateUrlWithFilters(location, field, urlValue, currentQuery)
        }

        // Prepare filters for API call - service_id is already an array
        const apiFilters = { ...newFilters }

        // Trigger data refresh with cleaned filters
        const finalFilters = cleanFilters(apiFilters)
        fetchData(finalFilters)
    }

    const handleSearch = () => {
        fetchData(cleanFilters(filters))
    }

    const handleClear = () => {
        const clearedFilters: FilterRecord = {}
        fields.forEach((field) => {
            clearedFilters[field.key] = initialFilters[field.key] || ''
        })
        setFilters(clearedFilters)

        // Clear URL parameters
        clearUrlParameters(location)

        // Clear Redux state and fetch all data
        fetchData(cleanFilters(clearedFilters))
    }

    const renderField = (field: FilterField) => {
        const { key, type, placeholder, options, width = 'w-[210px]', customComponent } = field

        if (customComponent) {
            return (
                <div key={key} className={width}>
                    {customComponent}
                </div>
            )
        }

        switch (type) {
            case 'select':
                return (
                    <div key={key} className={width}>
                        <SelectSimple
                            placeholder={placeholder}
                            value={filters[key] as FilterPrimitive}
                            onChange={(e: SelectSimpleChangeEvent) =>
                                handleFilterChange(
                                    key,
                                    e.target.value === null ? '' : String(e.target.value),
                                )
                            }
                            options={options || []}
                        />
                    </div>
                )

            case 'date':
                return (
                    <div key={key} className={width}>
                        <InputField
                            type="date"
                            value={filters[key] as FilterPrimitive}
                            onChange={(e) => handleFilterChange(key, e.target.value)}
                        />
                    </div>
                )

            case 'text':
                return (
                    <div key={key} className={width}>
                        <InputField
                            type="text"
                            value={filters[key] as FilterPrimitive}
                            onChange={(e) => handleFilterChange(key, e.target.value)}
                        />
                    </div>
                )

            case 'service':
                const filterValue = filters[key]
                const serviceValue = Array.isArray(filterValue)
                    ? filterValue.map((id: FilterPrimitive) =>
                          typeof id === 'string'
                              ? parseInt(id, 10)
                              : typeof id === 'number'
                                ? id
                                : 0,
                      ) // Convert strings to numbers
                    : filterValue
                      ? [
                            typeof filterValue === 'string'
                                ? parseInt(filterValue, 10)
                                : typeof filterValue === 'number'
                                  ? (filterValue as number)
                                  : 0,
                        ]
                      : []

                return (
                    <div key={key} className={width}>
                        {servicesLoading ? (
                            <div className="flex items-center justify-center h-10 px-3 border border-neutral-300 rounded">
                                <LoadingDots />
                            </div>
                        ) : (
                            <ServicesSelect
                                key={`${key}-${
                                    servicesData ? Object.keys(servicesData).length : 0
                                }`}
                                value={serviceValue}
                                onChangeValue={(value: string | string[]) => {
                                    // ServicesSelect sends an array, use it directly for multiple selections
                                    const serviceValue = Array.isArray(value) ? value : [value]
                                    handleFilterChange(key, serviceValue)
                                }}
                                placeholder={placeholder || 'Select Service'}
                            />
                        )}
                    </div>
                )

            default:
                return null
        }
    }

    return (
        <>
            <div className="flex gap-4 flex-wrap items-end">
                {fields.map(renderField)}
                <div className="flex gap-2.5 items-end">
                    <button
                        onClick={handleSearch}
                        disabled={isLoading}
                        className="px-4 py-2 bg-primary text-white border-none rounded cursor-pointer h-10 disabled:cursor-not-allowed hover:bg-primary-600 disabled:hover:bg-primary">
                        {isLoading ? <LinearLoader /> : 'Search'}
                    </button>
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 bg-grey-700 text-white border-none rounded cursor-pointer h-10 hover:bg-grey-800">
                        Clear
                    </button>
                </div>
                {showPagination && pagination && pagination.total > 0 && (
                    <div className="flex flex-col items-center gap-2 text-md text-grey-700">
                        <span>
                            Showing {pagination.page * pagination.limit + 1} to{' '}
                            {Math.min(
                                pagination.page * pagination.limit + pagination.limit,
                                pagination.total,
                            )}{' '}
                            of {pagination.total}
                        </span>
                        <div className="flex gap-1 w-full">
                            <button
                                onClick={() => onPageChange(pagination.page - 1)}
                                disabled={pagination.page === 0}
                                className="flex-1 px-2 py-1 text-sm bg-primary text-white border-none rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 hover:bg-grey-300 disabled:hover:bg-grey-200">
                                Previous
                            </button>
                            <button
                                onClick={() => onPageChange(pagination.page + 1)}
                                disabled={pagination.page >= pagination.pages - 1}
                                className="flex-1 px-2 py-1 text-sm bg-primary text-white border-none rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 hover:bg-grey-300 disabled:hover:bg-grey-200">
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default ReportFilter
